"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { site } from "@/lib/site";
import { attributionParams, CLICK_ID_PARAMS } from "@/lib/attribution";
import { advertisingAllowed, onConsentChange } from "@/lib/consent";

// Carries attribution from the landing URL through to every link that points at
// the app, so a visitor who signs up stays attributable across the origin hop.
// Two kinds of param travel:
//
//   - the referral pair `r` / `rs`, captured once and kept for the session in
//     sessionStorage, so a referral share survives navigation within the
//     landing site.
//   - the ad-platform click ids and UTMs (see ATTRIBUTION_PARAMS), read from the
//     URL and falling back to the durable first-touch cookie.
//
// Stamped both proactively on mount and again at click time; the latter survives
// client re-renders that would otherwise reset the href back to the prerendered
// value. Patching the DOM after mount rather than building the href during render
// is deliberate: the pages are statically generated, so a render-time href would
// differ between the server and the client and trip a hydration mismatch.
const APP_HOST = new URL(site.appUrl).host;

// These are the platform's names, and the platform is the only reader, so they
// are not ours to choose: App\Http\Middleware\CaptureReferralCookie takes the
// code from `r` and the share surface from `rs`. It has never read `referral`.
//
// It was `referral` here until 2026-09-03, which meant a share link
// (trein-vertraging.nl/?r=CODE, built by ClaimController) was read by nobody on
// the way in and stamped as nobody's name on the way out. Between the Next.js
// apex going live on 08-31 and the fix, users.referred_by went 11 -> 2 -> 0 -> 0
// a day while referral_share_clicked went UP, 42 -> 72. Both repos' suites were
// green throughout: this file's own test asserted the wrong name.
//
// `r` is deliberately NOT added to ATTRIBUTION_PARAMS in lib/attribution: there
// `r` is already the first-touch cookie's external-referrer key, validated as an
// http(s) URL, and a referral code in that slot both fails that validation and
// suppresses the real referrer. Keeping the referral pair local to this file is
// what keeps the two meanings of `r` apart.
const CODE_KEY = "tvt_referral";
const SURFACE_KEY = "tvt_referral_surface";

/**
 * The referral pair for this session: what the URL carries, else what an
 * earlier page in this session carried.
 *
 * The pair is resolved and stored ATOMICALLY, which is the whole reason this
 * is one function rather than two lookups. Resolving `r` and `rs` independently
 * lets them come from different visits: after `?r=alice&rs=whatsapp`, a plain
 * `?r=bob` link would be forwarded as `r=bob&rs=whatsapp` and record a share
 * that never happened. A code on the URL is a new referral and replaces the
 * whole record, including clearing a surface the new link did not supply.
 *
 * A surface with no code is meaningless, so `rs` never travels alone.
 */
function sessionReferral(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const urlCode = params.get("r");

  if (urlCode) {
    const urlSurface = params.get("rs") ?? "";
    try {
      sessionStorage.setItem(CODE_KEY, urlCode);
      if (urlSurface) {
        sessionStorage.setItem(SURFACE_KEY, urlSurface);
      } else {
        sessionStorage.removeItem(SURFACE_KEY);
      }
    } catch {}

    return urlSurface ? { r: urlCode, rs: urlSurface } : { r: urlCode };
  }

  try {
    const storedCode = sessionStorage.getItem(CODE_KEY);
    if (!storedCode) return {};
    const storedSurface = sessionStorage.getItem(SURFACE_KEY);

    return storedSurface
      ? { r: storedCode, rs: storedSurface }
      : { r: storedCode };
  } catch {
    return {};
  }
}

// An explicit value already on the anchor always wins - the author meant it.
//
// The one exception is a click id we ourselves stamped and may no longer forward:
// no CTA is authored with a gclid on it, so any click id on an app-bound anchor
// came from here, and a visitor who withdraws advertising consent mid-session must
// not keep clicking links that still carry it.
function stamp(
  anchor: HTMLAnchorElement,
  params: Record<string, string>,
): void {
  let url: URL;
  try {
    url = new URL(anchor.href);
  } catch {
    return;
  }
  if (url.host !== APP_HOST) return;

  let changed = false;
  for (const key of CLICK_ID_PARAMS) {
    if (params[key] !== undefined || !url.searchParams.has(key)) continue;
    url.searchParams.delete(key);
    changed = true;
  }
  for (const [key, value] of Object.entries(params)) {
    if (url.searchParams.has(key)) continue;
    url.searchParams.set(key, value);
    changed = true;
  }
  if (changed) anchor.href = url.toString();
}

export function AttributionForwarder() {
  const pathname = usePathname();

  useEffect(() => {
    // Recomputed on every consent change as well as every route: granting
    // advertising has to start forwarding the remembered click id, and
    // withdrawing it has to stop, without waiting for a navigation.
    let params: Record<string, string> = {};

    const recompute = () => {
      params = attributionParams({ advertising: advertisingAllowed() });
      Object.assign(params, sessionReferral());

      // No early return on an empty set: with advertising withdrawn the pass has
      // nothing to add but may still have a click id to take back off.
      document
        .querySelectorAll<HTMLAnchorElement>("a[href]")
        .forEach((anchor) => stamp(anchor, params));
    };

    recompute();

    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.("a[href]");
      if (anchor) stamp(anchor as HTMLAnchorElement, params);
    };
    document.addEventListener("click", onClick, true);
    const unsubscribe = onConsentChange(recompute);
    return () => {
      document.removeEventListener("click", onClick, true);
      unsubscribe();
    };
  }, [pathname]);

  return null;
}
